import {Box, HStack, Text, VStack} from '@chakra-ui/react';
import {ShippingCartIcon} from '@/components/svg/ShippingCartIcon';
import {useClient} from '@/haligator-factory';
import {useHalCollection} from '@synako/haligator';
import {CheckLoaded} from '@/containers/CheckLoaded';
import {LoadingSpinner} from '@/components/Loading/LoadingSpinner';
import NiceModal from '@ebay/nice-modal-react';
import {guessTitreAdresseDeLivraison} from 'ff-common';
import {CartonIcon} from '@/components/svg/CartonIcon';
import {useTranslation} from 'react-i18next';
import {ModaleChangerAdresseLivraison} from './ModaleChangerAdresseLivraison';
import {PanierCommands} from 'ff-common/src/hooks/panier/use-panier-commands';
import {Panier, RapportMatch} from 'api-types/ffconnect';
import {AlerteMatchingPdf} from './AlerteMatchingPdf';

// Mode de sélection de l'adresse.
// Le badge indique le niveau de confiance dans le choix de l'adresse :
// `unique` = le client n'en a qu'une,
// `bm25_match` = la plus ressemblante au texte extrait (déduction, à relire),
// `default_fallback` = aucune adresse extraite, on prend la première adresse du client.
const modeAdresseLibelle = {
  unique: 'back_office.pdf_order_import.match.mode_unique',
  bm25_match: 'back_office.pdf_order_import.match.mode_bm25',
  default_fallback: 'back_office.pdf_order_import.match.mode_default',
};

export const PanierRecapAdresse: React.FC<{
  panier: Panier;
  panierWithCommands: PanierCommands;
  // Panier PDF uniquement : matching de l'adresse extraite du bon de commande.
  matchingAdresse?: RapportMatch['adresse_de_livraison'];
}> = ({panier, panierWithCommands, matchingAdresse}) => {
  const {t} = useTranslation();
  const codeAdresse = panier.livraison_tolede.code_adresse_de_livraison;
  const mode_de_rendu = panier.livraison_tolede.mode_de_rendu ?? 'livraison';

  const clientAdressesHook = useHalCollection(
    useClient().follow('adresses_de_livraison'),
  );
  const changerModeDeRenduOptionHook =
    panierWithCommands.changerModeDeRendu.options;

  const adresseRetrait =
    changerModeDeRenduOptionHook.data?.adresse_d_enlevement;

  return (
    <CheckLoaded
      hook={clientAdressesHook.pageHook}
      render={() => {
        if (clientAdressesHook.items === undefined) return <LoadingSpinner />;
        const stateAdresse = clientAdressesHook.items.find(
          (adresse) =>
            adresse.data.tolede.code_adresse_de_livraison === codeAdresse,
        );
        const tolede = stateAdresse?.data.tolede;
        const isLivraisonOuPrestaDirect =
          mode_de_rendu === 'livraison' || mode_de_rendu === 'presta_direct';
        const adresse = isLivraisonOuPrestaDirect
          ? tolede?.adresse
          : adresseRetrait;

        const blocAdresse = () => {
          if (!tolede) return <LoadingSpinner />;
          const {enseigne, nom_client} = guessTitreAdresseDeLivraison(tolede);
          return (
            adresse && (
              <VStack
                alignItems='start'
                gap='0'
              >
                {isLivraisonOuPrestaDirect && (
                  <>
                    {enseigne && (
                      <Text
                        fontWeight='semibold'
                        fontSize='sm'
                      >
                        {enseigne}
                      </Text>
                    )}
                    {nom_client && (
                      <Text
                        fontWeight='semibold'
                        fontSize='sm'
                      >
                        {nom_client}
                      </Text>
                    )}
                  </>
                )}
                {adresse.ligne1 !== undefined && (
                  <Text fontSize='sm'>{adresse.ligne1}</Text>
                )}
                {adresse.ligne2 !== undefined && (
                  <Text fontSize='sm'>{adresse.ligne2}</Text>
                )}
                <Text fontSize='sm'>
                  {adresse.code_postal} {adresse.ville}
                </Text>
              </VStack>
            )
          );
        };

        return (
          <VStack
            bg='white'
            alignItems='stretch'
            p='5'
            borderRadius='8px'
            border='1px solid'
            borderColor='gray.light'
            textColor='black'
          >
            <HStack>
              {isLivraisonOuPrestaDirect ? (
                <ShippingCartIcon
                  color='black'
                  height='18px'
                  width='18px'
                />
              ) : (
                <CartonIcon
                  color='black'
                  height='18px'
                  width='18px'
                />
              )}
              <Text fontSize='sm'>
                {t(`panier.recap.mode_de_rendu.${mode_de_rendu}`)}
              </Text>
              {changerModeDeRenduOptionHook.data?.bouton_modifier_actif && (
                <Text
                  textDecorationLine='underline'
                  ml='auto'
                  cursor='pointer'
                  onClick={() => {
                    NiceModal.show(ModaleChangerAdresseLivraison, {
                      codeAdresse,
                      changerModeDeRenduOptions:
                        changerModeDeRenduOptionHook.state,
                      validateAction:
                        panierWithCommands.changerModeDeRendu.submit,
                      states: clientAdressesHook.items,
                      mode_de_rendu,
                    });
                  }}
                >
                  {t('global.modifier')}
                </Text>
              )}
            </HStack>
            {blocAdresse()}
            {matchingAdresse && (
              <>
                <Box
                  alignSelf='start'
                  px='2'
                  py='1'
                  borderRadius='md'
                  fontSize='xs'
                  fontWeight='medium'
                  bg='gray.light'
                  color='gray.dark'
                >
                  {t(modeAdresseLibelle[matchingAdresse.mode])}
                </Box>
                {matchingAdresse.etat !== 'ok' && (
                  // Panier PDF uniquement : alerte sur l'adresse de livraison
                  <AlerteMatchingPdf
                    message={
                      matchingAdresse.est_match
                        ? `${t('back_office.pdf_order_import.match.adresse_extraite')} : ${matchingAdresse.adresse_extraite}`
                        : t(
                            'back_office.pdf_order_import.match.aucune_adresse_matchee',
                          )
                    }
                  />
                )}
              </>
            )}
          </VStack>
        );
      }}
    />
  );
};
